import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useNavigate } from 'react-router-dom'
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { useLogin } from '@/hooks/useAuth'
import { useToast } from "@/hooks/use-toast"
import { MessageSquare, Lock, User, Building2, Mail, AlertCircle, X } from 'lucide-react'
import { useRegisterTenant } from '@/hooks/useRegistration'
import { tenantLogin, getCurrentUser1 } from '@/lib/api/generated/sdk.gen'
import apiClient from '@/lib/api/client'

const loginSchema = z.object({
  username: z.string().min(1, '请输入用户名'),
  password: z.string().min(1, '请输入密码'),
})

type LoginForm = z.infer<typeof loginSchema>

interface LoginModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function LoginModal({ open, onOpenChange }: LoginModalProps) {
  const { toast } = useToast()
  const navigate = useNavigate()
  const loginMutation = useLogin()
  const registerMutation = useRegisterTenant()
  const [view, setView] = useState<'login' | 'register'>('login')
  const [loginError, setLoginError] = useState<string>('')
  const [registerError, setRegisterError] = useState<string>('')
  const [isLoggingIn, setIsLoggingIn] = useState(false)

  // 移除React Hook Form，使用原生表单处理
  const reset = () => {
    // 简单的重置逻辑
  }

  const onSubmit = async (data: LoginForm) => {
    // 防止重复提交
    if (isLoggingIn) return
    
    // 清除之前的错误
    setLoginError('')
    setIsLoggingIn(true)
    
    try {
      // 使用自定义的登录逻辑，避免useLogin hook中的自动跳转
      const res = await tenantLogin({ 
        client: apiClient, 
        body: { username: data.username, password: data.password }, 
        responseStyle: 'fields', 
        throwOnError: false 
      })
      
      const status = (res as any)?.response?.status
      if (status && status !== 200) {
        const msg = ((res as any)?.error as any)?.message || '用户名或密码错误'
        throw new Error(msg)
      }
      
      const payload = (res as any)?.data
      const token = payload?.accessToken || payload?.token || payload?.data?.accessToken || payload?.data?.token
      if (!token) throw new Error('用户名或密码错误')
      
      // 登录成功 - 存储token
      localStorage.setItem('tenant_token', token)
      
      // 登录后立即拉取当前用户
      try {
        const me = await getCurrentUser1({ client: apiClient, responseStyle: 'fields', throwOnError: false })
        const meData: any = (me as any)?.data ?? me
        // 通过自定义事件让全局 AuthProvider 刷新
        window.dispatchEvent(new CustomEvent('auth-user-updated', { detail: meData }))
      } catch (_) {
        // 忽略，走后续路由守卫触发的刷新
      }
      
      // 登录成功后的处理
      toast({ title: '登录成功', description: '欢迎回来' })
      
      // 先关闭模态框
      onOpenChange(false)
      reset()
      setLoginError('')
      
      // 延迟跳转，确保模态框完全关闭后再跳转
      setTimeout(() => {
        navigate('/dashboard', { replace: true })
      }, 200)
      
    } catch (error: any) {
      // 解析具体的错误信息
      let errorMessage = '用户名或密码错误，请重试'
      
      if (error?.message) {
        if (error.message.includes('登录失败次数过多')) {
          errorMessage = '登录失败次数过多，请15分钟后重试'
        } else if (error.message.includes('用户名或密码错误')) {
          errorMessage = '用户名或密码错误，请检查后重试'
        } else if (error.message.includes('账号被锁定')) {
          errorMessage = '账号已被锁定，请联系管理员'
        } else if (error.message.includes('账号不存在')) {
          errorMessage = '账号不存在，请检查用户名或注册新账号'
        } else {
          errorMessage = error.message
        }
      }
      
      setLoginError(errorMessage)
    } finally {
      setIsLoggingIn(false)
    }
  }

  // 注册表单（账号密码 + 基本信息）
  const [regForm, setRegForm] = useState({
    account: '',
    password: '',
    tenantName: '',
    contactName: ''
  })
  const handleRegChange = (field: string, value: string) => {
    setRegForm(prev => ({ ...prev, [field]: value }))
    setRegisterError('') // 清除注册错误
  }
  
  const handleViewChange = (newView: 'login' | 'register') => {
    setView(newView)
    setLoginError('') // 清除登录错误
    setRegisterError('') // 清除注册错误
  }
  const onRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await registerMutation.mutateAsync({
        account: regForm.account,
        password: regForm.password,
        tenantName: regForm.tenantName,
        contactName: regForm.contactName,
      } as any)
      // 成功后无需提示“等待审核”，直接进入后续流程
      setRegForm({ account: '', password: '', tenantName: '', contactName: '' })
      setView('login')
    } catch (err: any) {
      let errorMessage = '注册失败，请稍后再试'
      
      if (err?.message) {
        if (err.message.includes('已存在')) {
          errorMessage = '该账号已存在，请直接登录或使用其他账号'
        } else if (err.message.includes('格式')) {
          errorMessage = '账号格式不正确，请使用邮箱或手机号'
        } else if (err.message.includes('密码')) {
          errorMessage = '密码格式不正确，请使用8位以上密码'
        } else {
          errorMessage = err.message
        }
      }
      
      setRegisterError(errorMessage)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-blue-600 rounded-full">
              <MessageSquare className="h-6 w-6 text-white" />
            </div>
          </div>
          {view === 'login' ? (
            <>
              <DialogTitle className="text-xl font-bold">登录租户账号</DialogTitle>
              <DialogDescription>请输入您的租户账号信息</DialogDescription>
            </>
          ) : (
            <>
              <DialogTitle className="text-xl font-bold">注册租户账号</DialogTitle>
              <DialogDescription>填写企业与联系人信息，提交后完成入驻</DialogDescription>
            </>
          )}
        </DialogHeader>
        {view === 'login' ? (
          <>
            {loginError && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="flex items-center justify-between">
                  <span>{loginError}</span>
                  <button
                    onClick={() => setLoginError('')}
                    className="ml-2 hover:bg-red-100 rounded p-1"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </AlertDescription>
              </Alert>
            )}
            <form onSubmit={(e) => {
              e.preventDefault() // 阻止表单默认提交行为
              e.stopPropagation() // 阻止事件冒泡
              
              const formData = new FormData(e.currentTarget)
              const username = formData.get('username') as string
              const password = formData.get('password') as string
              
              if (!username || !password) {
                setLoginError('请输入用户名和密码')
                return
              }
              
              onSubmit({ username, password })
            }} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">用户名</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input id="username" name="username" type="text" placeholder="请输入用户名 (demo)" className="pl-10" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">密码</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input id="password" name="password" type="password" placeholder="请输入密码 (demo)" className="pl-10" />
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={isLoggingIn}>
                {isLoggingIn ? '登录中...' : '登录'}
              </Button>
            </form>
            <div className="text-center text-sm text-gray-600 mt-3">
              <button className="text-blue-600 hover:underline" onClick={() => handleViewChange('register')}>没有账号？立即注册</button>
              <p className="mt-2">演示账号：demo / demo</p>
            </div>
          </>
        ) : (
          <>
            {registerError && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="flex items-center justify-between">
                  <span>{registerError}</span>
                  <button
                    onClick={() => setRegisterError('')}
                    className="ml-2 hover:bg-red-100 rounded p-1"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </AlertDescription>
              </Alert>
            )}
            <form onSubmit={onRegister} className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="account">账号（邮箱或手机号） *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input id="account" type="text" className="pl-10" value={regForm.account} onChange={(e) => handleRegChange('account', e.target.value)} required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="regPassword">设置密码（≥8位） *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input id="regPassword" type="password" minLength={8} className="pl-10" value={regForm.password} onChange={(e) => handleRegChange('password', e.target.value)} required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tenantName">企业名称 *</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input id="tenantName" className="pl-10" value={regForm.tenantName} onChange={(e) => handleRegChange('tenantName', e.target.value)} required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="contactName">联系人姓名 *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input id="contactName" className="pl-10" value={regForm.contactName} onChange={(e) => handleRegChange('contactName', e.target.value)} required />
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={registerMutation.isPending}>
                {registerMutation.isPending ? '提交中...' : '提交注册'}
              </Button>
            </form>
            <div className="text-center text-sm text-gray-600 mt-3">
              <button className="text-blue-600 hover:underline" onClick={() => handleViewChange('login')}>已有账号？去登录</button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}